import React, { useEffect, useState } from 'react';
import ROUTES from '../routes.json';
import { Link } from 'react-router-dom';
import { normalizeRoute } from '../utils/routeHelpers';
import PageHelmet from '../components/PageHelmet';
import { Helmet } from 'react-helmet-async';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/^['"]+|['"]+$/g, '').trim() || '';

function AiNewsList({ onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/news`);
        const j = await res.json();
        if (j && j.items) setItems(j.items);
      } catch (e) {
        console.error('AiNewsList load', e && e.message);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div>Loading AI news…</div>;
  return (
    <div className="ai-news-list">
      <PageHelmet
        title="AI News — Daily Summaries & Updates | OneAIHuman"
        description="Curated daily AI news summaries and concise highlights to keep you up to date with research, tools, and industry announcements."
        canonical={window.location.origin + normalizeRoute(ROUTES.aiNews.path)}
      />
      <h1>AI News</h1>
      <div className="page-content" style={{ marginTop: 12, color: '#6b7280' }}>
        <p>
          OneAI's AI News provides concise summaries and curated highlights from the world of artificial intelligence. We humanize and summarize incoming reports so you can quickly scan important developments, research breakthroughs, and tool announcements without wading through long technical posts.
        </p>
        <p>
          The feed is updated regularly and includes links to original sources so you can dive deeper. Use these summaries to stay informed, spot trends, and share short notes with your team.
        </p>
        <ul style={{ marginTop: 8 }}>
          <li>Curated AI news summaries for quick reading.</li>
          <li>Original source links included for deeper research.</li>
          <li>Daily updates to keep you informed of trends.</li>
        </ul>
      </div>
      <ul>
        {items.map(it => (
          <li key={it.slug} style={{marginBottom:12}}>
            <Link to={normalizeRoute(`${ROUTES.aiNews.path}${it.slug}/`)}>{it.title}</Link>
            <div style={{color:'#666'}}>{new Date(it.publishedAt).toLocaleString()} — {it.sourceName}</div>
            <p style={{marginTop:6}}>{it.summary}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AiNewsDetail({ slug }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/news/${slug}`);
        const j = await res.json();
        if (j && j.item) setItem(j.item);
      } catch (e) { console.error('AiNewsDetail', e && e.message); }
      finally { setLoading(false); }
    };
    if (slug) load();
  }, [slug]);

  if (loading) return <div>Loading…</div>;
  if (!item) return <div>Article not found.</div>;

  const jsonld = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: item.title,
    datePublished: item.publishedAt,
    author: { '@type': 'Organization', name: 'OneAI Humanize', url: window.location.origin },
    mainEntityOfPage: { '@type': 'WebPage', '@id': item.backlinkUrl },
  };

  return (
    <article className="ai-news-article">
      <PageHelmet
        title={item.title + ' — AI News'}
        description={item.summary || item.excerpt || (item.title + ' — AI news and summaries')}
        canonical={item.backlinkUrl || (window.location.origin + normalizeRoute(`${ROUTES.aiNews.path}${slug}/`))}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
      </Helmet>
      <h1>{item.title}</h1>
      <div style={{color:'#666'}}>{new Date(item.publishedAt).toLocaleString()} — <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">original</a></div>
      <p style={{marginTop:12}}>{item.summary}</p>
      <p>Read more and related tools at <a href="https://oneaihuman.com">OneAI Humanize</a></p>
    </article>
  );
}

export default function AiNews() {
  const path = (window.location.pathname || '').replace(/\/+$/, '');
  const base = ROUTES.aiNews.path.replace(/\/+$/, '');
  const maybe = path.startsWith(base + '/') ? path.substring((base + '/').length) : null;

  return maybe ? <AiNewsDetail slug={maybe} /> : <AiNewsList />;
}
